import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Platform } from 'react-native';
import {
  Banner,
  Footer,
  KeyboardScreenContainer,
  RadioGroup,
  Text,
} from '~/components';
import { useForm } from 'react-hook-form';
import type { MarisaAutoStepsProps } from '../..';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import useAppNavigation from '~/hooks/useAppNavigation';
import Skeleton from '~/components/Skeleton';
import Loader from './components/Loader';

interface StorageLocalQuizForm {
  storageLocal: string;
}

interface QuizProps {
  questionId?: string;
  step: number;
}

const QuizStorage = ({
  onContinue,
  questionId: questionCode,
  step,
}: MarisaAutoStepsProps & QuizProps) => {
  const navigation = useAppNavigation();
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<StorageLocalQuizForm>({
    resolver: yupResolver(
      yup
        .object()
        .shape({
          storageLocal: yup.string().required(),
        })
        .required()
    ),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { form, setStorageQuizAnswer, loadStorageQuizQuestions } =
    useMarisaAutoForm();

  let questionId = questionCode || Object.keys(form!.storageVehicle)[0];

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function loadQuizStorage() {
      setIsLoading(true);

      await loadStorageQuizQuestions();

      setIsLoading(false);
    }

    loadQuizStorage();
  }, [loadStorageQuizQuestions]);

  const config = useMemo(() => {
    if (!questionId) {
      return null;
    }
    return form!.storageVehicle[questionId];
  }, [form, questionId]);

  const onSubmit = useCallback(
    ({ storageLocal }: StorageLocalQuizForm) => {
      setStorageQuizAnswer({
        question: questionId!,
        response: storageLocal,
      });
      const questions = form!.storageVehicle;
      const questionsKeyArray = Object.keys(questions);
      const currentQuestionIndex = questionsKeyArray.findIndex(
        (key) => key === questionId
      );
      if (currentQuestionIndex < 0) {
        return;
      }
      const isLastQuestion =
        currentQuestionIndex === questionsKeyArray.length - 1;
      if (isLastQuestion) {
        onContinue();
      } else {
        navigation.push('MarisaServicos_MarisaAutoContract', {
          step: step,
          question: questionsKeyArray[currentQuestionIndex + 1],
        });
      }
    },
    [setStorageQuizAnswer, questionId, form, onContinue, navigation, step]
  );

  return (
    <KeyboardScreenContainer
      scrollEnabled={!isLoading}
      footer={
        <View
          className={`flex xl:hidden ${
            Platform.OS === 'web' ? 'py-4 px-6' : ''
          }`}
        >
          <Footer disabled={!isValid} onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="marisa-auto" />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
          <Skeleton loader={<Loader />} loading={isLoading}>
            <Text className="text-xl mb-4 text-gray-600">
              {config?.question || ''}
            </Text>

            <RadioGroup
              name={'storageLocal'}
              control={control}
              items={config?.answers || []}
            />

            <Footer
              disabled={!isValid}
              className="hidden"
              onSubmit={handleSubmit(onSubmit)}
            />
          </Skeleton>
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default QuizStorage;
